
import {Vue, Component} from 'vue-property-decorator';
import Pagination from "@/components/Pagination/index.vue";
import {
  detailQueryPiPackagingDetailHeadInfo, downloadPiMatchedPackagingDetail, downloadPiPackaging,
  listQueryPiMatchedPackagingStatistics,
  pageQueryPiMatchedPackagingDetail
} from "@/api/customer-package";

@Component({
  name: 'piPackageDetail',
  components: {Pagination},
})
export default class piPackageDetail extends Vue {
  private searchParams = {
    numberKeyword: '',
    providers: ["SUPPLIER","HQ"],
  };
  private headerInfo = {
    piId: "",
    piCode: "",
    seller: "",
    customer: "",
    orderDate: "",
  };
  private tableLoading = false;
  private tableRenderKey = 0;
  private tableData = [];
  private page = {
    pageNumber: 1,
    pageSize: 20,
    total: 0,
  }

  private readonly baseMergeProps = new Set([
    'piSN',
    'brand',
    'hqName',
    'codeType',
    'code',
    'supplier',
    'purchaseOrderCode',
    'productQuantity',
    'bagMethod',
    'bagProvider',
    'bagQty',
    'boxMethod',
    'boxProvider',
    'boxQty'
  ]);
  private readonly bagMergeProps = new Set([
    'bagNeutralSize',
    'bagUseSize',
    'bagCode',
    'bagName',
    'bagSpec',
    'bagPrice',
    'bagCount',
    'needBagCount',
    'bagRemark',
  ]);
  private readonly boxMergeProps = new Set([
    'boxNeutralSize',
    'boxUseSize',
    'boxCode',
    'boxName',
    'boxSpec',
    'boxPrice',
    'boxCount',
    'needBoxCount',
    'boxRemark',
  ]);
  private readonly bagProps = new Set([
    'bagMethod',
    'bagProvider',
    'bagQty',
    'bagNeutralSize',
    'bagUseSize',
    'bagCode',
    'bagName',
    'bagSpec',
    'bagPrice',
    'bagCount',
    'needBagCount',
    'bagRemark',
  ]);
  private readonly boxProps = new Set([
    'boxMethod',
    'boxProvider',
    'boxQty',
    'boxNeutralSize',
    'boxUseSize',
    'boxCode',
    'boxName',
    'boxSpec',
    'boxPrice',
    'boxCount',
    'needBoxCount',
    'boxRemark',
  ]);
  private packageStatisticsDialog = false;
  private packageStatisticsTableData = [];
  private isFirstActivated = true;

  created() {
    this.initHeaderInfoFromRoute();
    this.getHeadInfo();
    this.getList();
  }

  activated() {
    if (this.isFirstActivated) {
      this.isFirstActivated = false;
      return;
    }
    this.initHeaderInfoFromRoute();
    this.getHeadInfo();
    this.getList();
  }

  private initHeaderInfoFromRoute() {
    const item = this.$route.query.item as string;
    if (!item) {
      return;
    }
    const json = decodeURIComponent(item);
    const data = JSON.parse(json);
    this.headerInfo.piId = data.piId;
  }

  private async getHeadInfo() {
    const res = await detailQueryPiPackagingDetailHeadInfo(this.headerInfo.piId) as any;
    this.headerInfo.piCode = res.code;
    this.headerInfo.seller = res.seller;
    this.headerInfo.customer = res.customerAbbr;
    this.headerInfo.orderDate = res.orderDate;
  }

  private async getList() {
    this.tableLoading = true;
    try {
      const params = {
        numberKeyword: this.searchParams.numberKeyword ? this.searchParams.numberKeyword : undefined,
        providers: this.searchParams.providers && this.searchParams.providers.length > 0 ? this.searchParams.providers : undefined,
        pageNumber: this.page.pageNumber,
        pageSize: this.page.pageSize,
      }
      const res = await pageQueryPiMatchedPackagingDetail(this.headerInfo.piId, params) as any;
      this.page.total = Number(res.total);
      const list = res.list;
      this.tableRenderKey = Date.now();
      this.tableData = this.handleTableData(list);
    } finally {
      this.tableLoading = false;
    }
  }

  private handleTableData(list) {
    const tableData = [];
    for (const item of list) {
      const piSN = item.sn;
      const brand = item.brand;
      const purchaseOrderCode = item.purchaseOrderNumber;
      const productQuantity = item.totalQty;
      const hqProductName = item.hqProductName;
      const inquiryRefType = item.inquiryRefType;
      const inquiryRefNumber = item.inquiryRefNumber;
      const supplierAbbr = item.supplierAbbr;

      const innerBagLength = item.innerBag.details?.length || 1;
      const innerBoxLength = item.innerBox.details?.length || 1;
      const rowCount = Math.max(innerBagLength, innerBoxLength);
      const max = rowCount;
      const min = Math.min(innerBagLength, innerBoxLength);
      const lineMerge = Math.floor(Number((max / min).toFixed(2)));
      let lastLineMergeMore = max % min;
      let maxPackage;
      if (innerBagLength > innerBoxLength) {
        maxPackage = "bag";
      } else {
        maxPackage = "box";
      }
      let needMergeIndex = 0;
      let index = -1;
      for (let i = 0; i < rowCount; i++) {
        let merge = 0;
        if (needMergeIndex === i) {
          if (lastLineMergeMore !== 0) {
            merge = lineMerge + 1;
            lastLineMergeMore = lastLineMergeMore - 1;
          } else {
            merge = lineMerge;
          }
          needMergeIndex = needMergeIndex + merge;
        }
        if (merge !== 0) {
          index++;
        }
        const bagIndex = maxPackage === "bag"? i : index;
        const boxIndex = maxPackage === "box"? i : index;
        const baseMerge = i === 0? rowCount : 0;
        let bagMerge;
        let boxMerge;
        if (maxPackage === "bag") {
          bagMerge = 1;
        } else {
          bagMerge = merge;
        }
        if (maxPackage === "box") {
          boxMerge = 1;
        } else {
          boxMerge = merge;
        }
        const innerBagDetail = item.innerBag.details && item.innerBag.details.length > 0? item.innerBag.details[bagIndex] : {};
        const innerBoxDetail = item.innerBox.details && item.innerBox.details.length > 0? item.innerBox.details[boxIndex] : {};
        const data = {
          rowKey: `${piSN}-${purchaseOrderCode}-${i}-${bagIndex}-${boxIndex}`,
          piSN: piSN,
          brand: brand,
          purchaseOrderCode: purchaseOrderCode,
          productQuantity: productQuantity,
          hqName: hqProductName,
          codeType: inquiryRefType,
          code: inquiryRefNumber,
          supplier: supplierAbbr,
          bagMethod: item.innerBag.desc,
          bagProvider: item.innerBag.provider,
          bagQty: item.innerBag.itemsPerPack,
          bagNeutralSize: innerBagDetail.supplierDimension,
          bagUseSize: innerBagDetail.actualDimension,
          bagCode: innerBagDetail.code,
          bagName: innerBagDetail.name,
          bagSpec: innerBagDetail.specification,
          bagPrice: innerBagDetail.unitPrice,
          bagCount: innerBagDetail.quantity,
          needBagCount: innerBagDetail.requiredQty,
          bagRemark: item.innerBag.remark,
          boxMethod: item.innerBox.desc,
          boxProvider: item.innerBox.provider,
          boxQty: item.innerBox.itemsPerPack,
          boxNeutralSize: innerBoxDetail.supplierDimension,
          boxUseSize: innerBoxDetail.actualDimension,
          boxCode: innerBoxDetail.code,
          boxName: innerBoxDetail.name,
          boxSpec: innerBoxDetail.specification,
          boxPrice: innerBoxDetail.unitPrice,
          boxCount: innerBoxDetail.quantity,
          needBoxCount: innerBoxDetail.requiredQty,
          boxRemark: item.innerBox.remark,
          baseMerge: baseMerge,
          bagMerge: bagMerge,
          boxMerge: boxMerge,
        };
        tableData.push(data);
      }
    }
    return tableData;
  }

  // 合并单元格
  private objectSpanMethod({ row, column }) {
    const prop = column && (column as any).property;
    if (this.baseMergeProps.has(prop)) {
      return { rowspan: row.baseMerge, colspan: 1 }
    }
    if (this.baseMergeProps.has(prop)) {
      return { rowspan: row.bagMerge, colspan: 1 }
    }
    if (this.boxMergeProps.has(prop)) {
      return { rowspan: row.boxMerge, colspan: 1 }
    }
    return { rowspan: 1, colspan: 1 }
  }

  // 表头样式
  private headerCellStyle({ rowIndex, column }) {
    if (rowIndex !== 0) {
      return {}
    }
    const label = column && (column as any).label;
    if (label === '内袋') {
      return { backgroundColor: '#409EFF', color: '#fff', fontWeight: 'bold' }
    }
    if (label === '内盒') {
      return { backgroundColor: '#E6A23C', color: '#fff', fontWeight: 'bold' }
    }
    return {}
  }

  // 单元格样式
  private cellStyle({ column }) {
    const prop = column && (column as any).property;
    if (this.bagProps.has(prop)) {
      return { backgroundColor: '#E8F4FF' }
    }
    if (this.boxProps.has(prop)) {
      return { backgroundColor: '#FFF3E6' }
    }
    return {}
  }

  private async downloadList() {
    const params = {
      numberKeyword: this.searchParams.numberKeyword,
      providers: this.searchParams.providers,
    }
    await downloadPiMatchedPackagingDetail(this.headerInfo.piId, params);
  }

  private async packageStatistics() {
    this.packageStatisticsTableData = await listQueryPiMatchedPackagingStatistics(this.headerInfo.piId) as any;
    this.packageStatisticsDialog = true;
  }
}
